import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";

import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TextField,
    MenuItem,
    Grid,
    Chip,
    Button,
    Menu,
    ListItemIcon,
    ListItemText,
    Select,
    Checkbox,
    ListItemText as MuiListItemText,
    FormControl,
    InputLabel,
    Card,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Pagination,
    Autocomplete,
    LinearProgress,
    Alert,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import PushPinIcon from "@mui/icons-material/PushPin";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import { apiGetPosts } from "../../../services/apiGetPosts";
import { apiApprovePost, apiRejectPost, apiRejectPosts, apiDeletePost } from "../../../services/apiPostStatus";
import { apiUpdatePost } from "../../../services/apiUpdatePost";
import { apiPostStarRating } from "../../../services/apiPostStarRating";
import { apiPostRemovePontuacaoDesafio } from "../../../services/apiPostRemovePontuacaoDesafio";
import { apiGetPostThemes, apiGetPostFormats } from "../../../services";

import { useAuthStore } from "../../../store/useAuthStore";
import { useFormattedText } from "../../../hooks/useFormattedText";

import { Loader } from "../../../components/ui/Loader";

import { POST_STATUS } from "../../../data/Interfaces";

const statusList = [{ value: "TODOS", label: "Todos" }, ...POST_STATUS];

const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];
const DEFAULT_ROWS_PER_PAGE = 100;
const BULK_CONCURRENCY = 4;
const TIMED_PROGRESS_TICK_MS = 200;
const TIMED_PROGRESS_CEILING = 95;
const TIMED_PROGRESS_HALF_LIFE_S = 6;

const getStatusChipColor = (status) => {
    switch (status) {
        case "APROVADO":
            return { bg: "rgba(46, 125, 50, 0.1)", color: "#2E7D32", borderColor: "#2E7D32", tableBg: "#E8F5E9" };
        case "REPROVADO":
            return { bg: "rgba(198, 40, 40, 0.1)", color: "#C62828", borderColor: "#C62828", tableBg: "#FFEBEE" };
        case "PENDENTE":
            return { bg: "rgba(239, 108, 0, 0.1)", color: "#EF6C00", borderColor: "#EF6C00", tableBg: "#FFF3E0" };
        default:
            return { bg: "rgba(97, 97, 97, 0.1)", color: "#616161", borderColor: "#616161", tableBg: "#E0E0E0" };
    }
};

const getStatusLabel = (statusValue) => {
    const status = POST_STATUS.find((s) => s.value === statusValue);
    return status ? status.label : POST_STATUS.find((s) => s.value === "PENDENTE").label;
};

const getStatusValue = (label) => {
    const status = POST_STATUS.find((s) => s.label === label);
    return status ? status.value : "PENDENTE";
};

const MENU_ITEMS = {
    approve: {
        action: "approve",
        icon: <CheckCircleIcon fontSize="small" />,
        text: "Aprovar",
    },
    reject: {
        action: "reject",
        icon: <CancelIcon fontSize="small" />,
        text: "Reprovar",
    },
    delete: {
        action: "delete",
        icon: <DeleteIcon fontSize="small" />,
        text: "Excluir",
    },
    pin: {
        action: "pin",
        icon: <PushPinOutlinedIcon fontSize="small" />,
        text: "Pinar",
    },
    unpin: {
        action: "unpin",
        icon: <PushPinIcon fontSize="small" />,
        text: "Despinar",
    },
    removePontuacao: {
        action: "removePontuacao",
        icon: <RemoveCircleOutlineIcon fontSize="small" />,
        text: "Remover Pontuação Desafio",
    },
};

const getAvailableActions = (status, pinado, post) => {
    const baseActions = [];

    switch (status) {
        case "PENDENTE":
            baseActions.push("approve", "reject", "delete");
            break;
        case "APROVADO":
            baseActions.push("reject", "delete");
            break;
        case "REPROVADO":
            baseActions.push("approve", "delete");
            break;
        default:
            baseActions.push("delete");
    }

    baseActions.push(pinado ? "unpin" : "pin");

    // Adiciona a opção de remover pontuação de desafio se o post tiver desafiocreator
    if (post?.desafiocreator > 0) {
        baseActions.push("removePontuacao");
    }

    return baseActions;
};

// The request duration is unknown, so the bar eases toward a ceiling it never reaches
const useTimedProgress = (active) => {
    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (!active) {
            setProgress(0);
            return undefined;
        }

        const startedAt = Date.now();
        const interval = setInterval(() => {
            const elapsedSeconds = (Date.now() - startedAt) / 1000;

            setProgress(TIMED_PROGRESS_CEILING * (1 - Math.exp(-elapsedSeconds / TIMED_PROGRESS_HALF_LIFE_S)));
        }, TIMED_PROGRESS_TICK_MS);

        return () => clearInterval(interval);
    }, [active]);

    return progress;
};

const BulkProgressDialog = ({ progress }) => {
    const timedProgress = useTimedProgress(Boolean(progress) && progress.value === null);

    return (
        <Dialog open={Boolean(progress)} disableEscapeKeyDown maxWidth="xs" fullWidth>
            <DialogTitle>{progress?.label}</DialogTitle>
            <DialogContent>
                <LinearProgress
                    variant="determinate"
                    value={progress?.value ?? timedProgress}
                    sx={{ height: 8, borderRadius: 4, mb: 2 }}
                />
                <Typography variant="body2" color="text.secondary">
                    Isso pode levar alguns instantes. Não feche esta janela.
                </Typography>
            </DialogContent>
        </Dialog>
    );
};

const BulkResultDialog = ({ result, onClose }) => (
    <Dialog open={Boolean(result)} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>{result?.title}</DialogTitle>
        <DialogContent>
            <Alert severity={result?.severity ?? "info"} icon={false} sx={{ mt: 1 }}>
                {result?.message}
            </Alert>
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose} variant="contained" autoFocus>
                OK
            </Button>
        </DialogActions>
    </Dialog>
);

const RejectReasonDialog = ({ open, title, description, confirmLabel, value, onChange, onClose, onConfirm }) => (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
            {description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {description}
                </Typography>
            )}
            <TextField
                autoFocus
                margin="dense"
                label="Digite o motivo da rejeição"
                type="text"
                fullWidth
                multiline
                rows={4}
                value={value}
                onChange={(event) => onChange(event.target.value)}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={onClose}>Cancelar</Button>
            <Button onClick={onConfirm} variant="contained" color="primary" disabled={!value.trim()}>
                {confirmLabel}
            </Button>
        </DialogActions>
    </Dialog>
);

const StarRating = ({ value, onChange }) => {
    const [rating, setRating] = useState(0);

    useEffect(() => {
        if (value) {
            const match = value.match(/ESTRELA_(\d)/);
            if (match) {
                setRating(parseInt(match[1]));
            }
        }
    }, [value]);

    const handleClick = (newRating) => {
        const nextRating = rating === newRating ? 0 : newRating;
        setRating(nextRating);
        onChange(nextRating === 0 ? null : `ESTRELA_${nextRating}`);
    };

    return (
        <Box
            sx={{
                display: "flex",
                gap: 1,
                position: "relative",
                zIndex: 2,
            }}
        >
            {[1, 2, 3].map((star) => (
                <button
                    key={star}
                    type="button"
                    onClick={() => handleClick(star)}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        fontSize: "24px",
                        padding: "4px",
                        color: rating >= star ? "#FFD700" : "#ccc",
                    }}
                >
                    ★
                </button>
            ))}
        </Box>
    );
};

export const PostsReport = ({ title = "Posts" }) => {
    const { id: comunidadeId } = useParams();
    const { creator } = useAuthStore();
    const { sanitizeContent, getTruncatedContent } = useFormattedText();
    const [allPosts, setAllPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
    const [filterAuthors, setFilterAuthors] = useState([]);
    const [filterThemes, setFilterThemes] = useState([]);
    const [filterFormats, setFilterFormats] = useState([]);
    const [availableThemes, setAvailableThemes] = useState([]);
    const [availableFormats, setAvailableFormats] = useState([]);
    const [filterStatus, setFilterStatus] = useState(["PENDENTE"]);
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedPost, setSelectedPost] = useState(null);
    const [isUpdated, setIsUpdated] = useState(false);
    const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
    const [selectedIds, setSelectedIds] = useState([]);
    const [bulkApproveDialogOpen, setBulkApproveDialogOpen] = useState(false);
    const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false);
    const [bulkProgress, setBulkProgress] = useState(null);
    const [bulkResult, setBulkResult] = useState(null);

    const fetchPosts = async () => {
        try {
            setLoading(true);
            setIsUpdated(false);
            const response = await apiGetPosts("ALL", comunidadeId);
            setAllPosts(Array.isArray(response) ? response : []);
            setIsUpdated(true);
        } catch (error) {
            console.error("Erro ao buscar posts:", error);
            setAllPosts([]);
            setIsUpdated(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();
    }, [comunidadeId]);

    useEffect(() => {
        const fetchThemesAndFormats = async () => {
            try {
                const [themesResponse, formatsResponse] = await Promise.all([apiGetPostThemes(), apiGetPostFormats()]);
                setAvailableThemes(themesResponse || []);
                setAvailableFormats(formatsResponse || []);
            } catch (error) {
                console.error("Erro ao buscar temas e formatos:", error);
            }
        };

        fetchThemesAndFormats();
    }, []);

    const addAuthorFilter = (author) => {
        setFilterAuthors((current) => (current.includes(author) ? current : [...current, author]));
    };

    const handleThemeChange = (event) => {
        const value = event.target.value;
        setFilterThemes(value.includes("todos") ? [] : value.filter((v) => v !== "todos"));
    };

    const handleFormatChange = (event) => {
        const value = event.target.value;
        setFilterFormats(value.includes("todos") ? [] : value.filter((v) => v !== "todos"));
    };

    const handleStatusChange = (event) => {
        const value = event.target.value;
        setFilterStatus(value.includes("todos") ? [] : value.filter((v) => v !== "todos"));
    };

    const handleStartDateChange = (event) => {
        setStartDate(event.target.value);
    };

    const handleEndDateChange = (event) => {
        setEndDate(event.target.value);
    };

    const handleSort = (key) => {
        let direction = "asc";
        if (sortConfig.key === key && sortConfig.direction === "asc") {
            direction = "desc";
        }
        setSortConfig({ key, direction });
    };

    const handleMenuOpen = (event, post) => {
        setAnchorEl(event.currentTarget);
        setSelectedPost(post);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleOpenRejectDialog = (post) => {
        setSelectedPost(post);
        setRejectDialogOpen(true);
        setAnchorEl(null);
    };

    const handleReject = async () => {
        if (!selectedPost) return;
        try {
            setIsUpdated(false);
            await apiRejectPost(selectedPost.id, rejectReason);
            await fetchPosts();
        } catch (error) {
            console.error("Error:", error);
        } finally {
            setRejectDialogOpen(false);
            setSelectedPost(null);
            setRejectReason("");
        }
    };

    const handleAction = async (action) => {
        if (!selectedPost) return;

        try {
            setIsUpdated(false);
            switch (action) {
                case "approve":
                    await apiApprovePost(selectedPost.id);
                    break;
                case "reject":
                    handleOpenRejectDialog(selectedPost);
                    break;
                case "delete":
                    await apiDeletePost(selectedPost.id);
                    break;
                case "pin":
                    await apiUpdatePost(selectedPost.id, { pinado: true });
                    break;
                case "unpin":
                    await apiUpdatePost(selectedPost.id, { pinado: false });
                    break;
                case "removePontuacao":
                    await apiPostRemovePontuacaoDesafio(selectedPost.id, selectedPost.desafiocreator);
                    break;
            }
            await fetchPosts();
        } catch (error) {
            console.error("Error:", error);
        } finally {
            handleMenuClose();
        }
    };

    const uniqueAuthors = React.useMemo(() => {
        const authors = allPosts
            .filter((post) => post?.creator?.nome)
            .map((post) => post.creator.nome)
            .filter((v, i, a) => a.indexOf(v) === i)
            .sort();
        return authors;
    }, [allPosts]);

    const filteredPosts = React.useMemo(() => {
        return allPosts.filter((post) => {
            const authorMatch = filterAuthors.length === 0 || filterAuthors.includes(post?.creator?.nome);
            const themeMatch = filterThemes.length === 0 || filterThemes.includes(post?.theme);
            const formatMatch = filterFormats.length === 0 || filterFormats.includes(post?.format);
            const statusMatch =
                filterStatus.includes("TODOS") || filterStatus.length === 0 || filterStatus.includes(post?.status);

            const normalizeDate = (date) => {
                if (!date) return null;
                const d = new Date(date);
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
            };

            const postDate = normalizeDate(post?.createdAt);
            const startDateObj = startDate;
            const endDateObj = endDate;

            const dateMatch =
                (!startDateObj || (postDate && postDate >= startDateObj)) &&
                (!endDateObj || (postDate && postDate <= endDateObj));

            return authorMatch && themeMatch && formatMatch && statusMatch && dateMatch;
        });
    }, [allPosts, filterAuthors, filterThemes, filterFormats, filterStatus, startDate, endDate]);

    const sortedPosts = React.useMemo(() => {
        if (!filteredPosts || !Array.isArray(filteredPosts)) return [];

        let sortedItems = [...filteredPosts];
        if (sortConfig.key) {
            sortedItems.sort((a, b) => {
                if (!a || !b) return 0;

                let valA = a[sortConfig.key];
                let valB = b[sortConfig.key];

                if (sortConfig.key === "creator.nome") {
                    valA = a?.creator?.nome;
                    valB = b?.creator?.nome;
                }

                if (valA < valB) {
                    return sortConfig.direction === "asc" ? -1 : 1;
                }
                if (valA > valB) {
                    return sortConfig.direction === "asc" ? 1 : -1;
                }
                return 0;
            });
        }
        return sortedItems;
    }, [filteredPosts, sortConfig]);

    const totalPages = Math.max(1, Math.ceil(sortedPosts.length / rowsPerPage));
    // Avoid setting the page to a value greater than totalPages
    const currentPage = Math.min(page, totalPages);

    useEffect(() => {
        setPage(1);
    }, [filterAuthors, filterThemes, filterFormats, filterStatus, startDate, endDate, rowsPerPage, sortConfig]);

    const paginatedPosts = React.useMemo(() => {
        const start = (currentPage - 1) * rowsPerPage;

        return sortedPosts.slice(start, start + rowsPerPage);
    }, [sortedPosts, currentPage, rowsPerPage]);

    const selectedIdSet = React.useMemo(() => new Set(selectedIds), [selectedIds]);

    const selectedPosts = React.useMemo(
        () => sortedPosts.filter((post) => selectedIdSet.has(post?.id)),
        [sortedPosts, selectedIdSet],
    );

    const selectionAllowing = (action) =>
        selectedPosts.filter((post) => getAvailableActions(post?.status, post?.pinado, post).includes(action));

    const approvableSelection = selectionAllowing("approve");
    const rejectableSelection = selectionAllowing("reject");

    const allOnPageSelected = paginatedPosts.length > 0 && paginatedPosts.every((post) => selectedIdSet.has(post?.id));
    const someOnPageSelected = paginatedPosts.some((post) => selectedIdSet.has(post?.id));

    useEffect(() => {
        setSelectedIds([]);
    }, [filterAuthors, filterThemes, filterFormats, filterStatus, startDate, endDate]);

    const toggleSelectPost = (postId) => {
        setSelectedIds((current) =>
            current.includes(postId) ? current.filter((id) => id !== postId) : [...current, postId],
        );
    };

    const toggleSelectPage = () => {
        const pageIds = paginatedPosts.map((post) => post?.id);

        setSelectedIds((current) =>
            allOnPageSelected ? current.filter((id) => !pageIds.includes(id)) : [...new Set([...current, ...pageIds])],
        );
    };

    const finishBulkAction = async (outcome) => {
        setSelectedIds([]);
        await fetchPosts();
        setBulkProgress(null);
        setBulkResult(outcome);
    };

    const handleBulkApprove = async () => {
        const postIds = approvableSelection.map((post) => post.id);
        const queue = [...postIds];
        const failedIds = [];

        setBulkApproveDialogOpen(false);
        setBulkProgress({ label: `Aprovando ${postIds.length} post(s)…`, value: 0 });

        const worker = async () => {
            while (queue.length > 0) {
                const postId = queue.shift();

                try {
                    await apiApprovePost(postId);
                } catch (error) {
                    console.error(`Erro ao aprovar o post ${postId}:`, error);
                    failedIds.push(postId);
                }

                const done = postIds.length - queue.length;

                setBulkProgress({
                    label: `Aprovando ${done} de ${postIds.length}…`,
                    value: (done / postIds.length) * 100,
                });
            }
        };

        await Promise.all(Array.from({ length: Math.min(BULK_CONCURRENCY, postIds.length) }, worker));

        await finishBulkAction(
            failedIds.length === 0
                ? {
                      severity: "success",
                      title: "Posts aprovados",
                      message: `${postIds.length} post(s) aprovados e pontuando no ranking.`,
                  }
                : {
                      severity: "warning",
                      title: "Aprovação concluída com falhas",
                      message: `${postIds.length - failedIds.length} de ${postIds.length} post(s) aprovados. ${failedIds.length} não puderam ser atualizados.`,
                  },
        );
    };

    // Single request so each author gets one email listing all of their posts
    const handleBulkReject = async () => {
        const postIds = rejectableSelection.map((post) => post.id);
        const motivo = rejectReason;

        setBulkRejectDialogOpen(false);
        setRejectReason("");
        setBulkProgress({ label: `Reprovando ${postIds.length} post(s)…`, value: null });

        let outcome;

        try {
            const result = await apiRejectPosts(postIds, motivo);
            const reprovados = result?.reprovados?.length ?? 0;
            const naoAtualizados = postIds.length - reprovados;

            if (naoAtualizados > 0) {
                console.error("Posts que não foram reprovados:", {
                    falhas: result?.falhas,
                    naoEncontrados: result?.naoEncontrados,
                });
            }

            outcome =
                naoAtualizados === 0
                    ? {
                          severity: "success",
                          title: "Posts reprovados",
                          message: `${reprovados} post(s) reprovados e pontuação removida. Cada autor recebeu um e-mail com a lista dos seus posts.`,
                      }
                    : {
                          severity: "warning",
                          title: "Reprovação concluída com falhas",
                          message: `${reprovados} de ${postIds.length} post(s) reprovados. ${naoAtualizados} não puderam ser atualizados.`,
                      };
        } catch (error) {
            console.error("Erro ao reprovar posts em massa:", error);
            outcome = {
                severity: "error",
                title: "Não foi possível concluir a reprovação",
                message: "A lista foi recarregada. Confira quais posts foram atualizados antes de tentar novamente.",
            };
        }

        await finishBulkAction(outcome);
    };

    const handlePageChange = (event, value) => {
        setPage(value);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleRowsPerPageChange = (event) => {
        setRowsPerPage(Number(event.target.value));
    };

    const renderFormattedText = (content) => {
        if (!content) return null;
        const sanitizedContent = sanitizeContent(content);
        return (
            <Box
                dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                sx={{
                    wordBreak: "break-word",
                    "& p": { margin: 0 },
                    maxHeight: "120px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                }}
            />
        );
    };

    // Stay mounted during a bulk action so the loader does not replace the progress dialog
    if (!isUpdated && !bulkProgress) {
        return <Loader />;
    }

    return (
        <Box>
            <Card sx={{ p: 3, mb: 3, mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {title}
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                        <Autocomplete
                            multiple
                            options={uniqueAuthors}
                            value={filterAuthors}
                            onChange={(event, newValue) => setFilterAuthors(newValue)}
                            filterSelectedOptions
                            renderTags={(value, getTagProps) =>
                                value.map((author, index) => (
                                    <Chip {...getTagProps({ index })} key={author} label={author} size="small" />
                                ))
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Autor"
                                    placeholder={filterAuthors.length === 0 ? "Todos os autores" : ""}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Tema</InputLabel>
                            <Select
                                multiple
                                value={filterThemes}
                                onChange={handleThemeChange}
                                renderValue={(selected) => selected.join(", ")}
                            >
                                <MenuItem key="todos" value="todos">
                                    <Checkbox checked={filterThemes.length === 0} />
                                    <MuiListItemText primary="Todos os temas" />
                                </MenuItem>
                                {availableThemes.map((theme) => (
                                    <MenuItem key={theme} value={theme}>
                                        <Checkbox checked={filterThemes.indexOf(theme) > -1} />
                                        <MuiListItemText primary={theme} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                        <FormControl fullWidth>
                            <InputLabel>Formato</InputLabel>
                            <Select
                                multiple
                                value={filterFormats}
                                onChange={handleFormatChange}
                                renderValue={(selected) => selected.join(", ")}
                            >
                                <MenuItem key="todos" value="todos">
                                    <Checkbox checked={filterFormats.length === 0} />
                                    <MuiListItemText primary="Todos os formatos" />
                                </MenuItem>
                                {availableFormats.map((format) => (
                                    <MenuItem key={format} value={format}>
                                        <Checkbox checked={filterFormats.indexOf(format) > -1} />
                                        <MuiListItemText primary={format} />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Data Início"
                            value={startDate}
                            onChange={handleStartDateChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={2}>
                        <TextField
                            fullWidth
                            type="date"
                            label="Data Fim"
                            value={endDate}
                            onChange={handleEndDateChange}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {statusList.map((status) => {
                            const isSelected = filterStatus.includes(status.value);
                            const chipColor = getStatusChipColor(status.value === "TODOS" ? "PENDENTE" : status.value);
                            return (
                                <Chip
                                    key={status.value}
                                    label={`${status.label} ${status.value !== "TODOS" ? `(${allPosts.filter((post) => post.status === status.value).length})` : ""}`}
                                    onClick={() => {
                                        if (status.value === "TODOS") {
                                            setFilterStatus(["TODOS"]);
                                        } else {
                                            const newStatus = isSelected
                                                ? filterStatus.filter((s) => s !== status.value)
                                                : [...filterStatus.filter((s) => s !== "TODOS"), status.value];
                                            setFilterStatus(newStatus.length ? newStatus : ["TODOS"]);
                                        }
                                    }}
                                    sx={{
                                        backgroundColor: isSelected ? chipColor.bg : "transparent",
                                        color: isSelected ? chipColor.color : chipColor.color,
                                        border: `1px solid ${isSelected ? chipColor.borderColor : chipColor.borderColor}`,
                                        "&:hover": {
                                            backgroundColor: isSelected ? `${chipColor.bg}` : `${chipColor.bg}50`,
                                        },
                                        cursor: "pointer",
                                        fontWeight: isSelected ? 500 : 400,
                                        height: "32px",
                                    }}
                                />
                            );
                        })}
                    </Box>
                </Box>
            </Card>

            {selectedIds.length > 0 && (
                <Card sx={{ p: 2, mb: 2, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                    <Typography variant="body2">{selectedIds.length} post(s) selecionado(s)</Typography>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<CheckCircleIcon />}
                        disabled={approvableSelection.length === 0 || Boolean(bulkProgress)}
                        onClick={() => setBulkApproveDialogOpen(true)}
                    >
                        Aprovar ({approvableSelection.length})
                    </Button>
                    <Button
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<CancelIcon />}
                        disabled={rejectableSelection.length === 0 || Boolean(bulkProgress)}
                        onClick={() => setBulkRejectDialogOpen(true)}
                    >
                        Reprovar ({rejectableSelection.length})
                    </Button>
                    <Button size="small" onClick={() => setSelectedIds([])} disabled={Boolean(bulkProgress)}>
                        Limpar seleção
                    </Button>
                </Card>
            )}

            <TableContainer component={Paper} sx={{ overflow: "auto" }}>
                <Table sx={{ minWidth: 650 }}>
                    <colgroup>
                        <col style={{ width: "48px" }} />
                        <col style={{ width: "80px" }} />
                        <col style={{ minWidth: "250px" }} />
                        <col style={{ minWidth: "150px" }} />
                        <col style={{ minWidth: "120px" }} />
                        <col style={{ minWidth: "100px" }} />
                        <col style={{ minWidth: "100px" }} />
                        <col style={{ minWidth: "120px" }} />
                        <col style={{ minWidth: "120px" }} />
                        <col style={{ minWidth: "150px" }} />
                        <col style={{ minWidth: "120px" }} />
                        <col style={{ minWidth: "120px" }} />
                        <col style={{ minWidth: "100px" }} />
                    </colgroup>
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    checked={allOnPageSelected}
                                    indeterminate={someOnPageSelected && !allOnPageSelected}
                                    onChange={toggleSelectPage}
                                />
                            </TableCell>
                            <TableCell>Cover</TableCell>
                            <TableCell onClick={() => handleSort("texto")} style={{ cursor: "pointer" }}>
                                Texto
                            </TableCell>
                            <TableCell onClick={() => handleSort("creator.nome")} style={{ cursor: "pointer" }}>
                                Autor
                            </TableCell>
                            <TableCell onClick={() => handleSort("createdAt")} style={{ cursor: "pointer" }}>
                                Data
                            </TableCell>
                            <TableCell>Comentários</TableCell>
                            <TableCell>Likes</TableCell>
                            <TableCell>Tema</TableCell>
                            <TableCell>Formato</TableCell>
                            <TableCell>Desafio</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell>Avaliação</TableCell>
                            <TableCell>Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedPosts.map((post) => (
                            <TableRow
                                key={post?.id}
                                sx={{
                                    "&:hover": {
                                        "& > td": {
                                            backgroundColor: "rgba(0, 0, 0, 0.04) !important",
                                        },
                                        "& .star-rating-cell": {
                                            backgroundColor: "transparent !important",
                                        },
                                    },
                                }}
                            >
                                <TableCell padding="checkbox" sx={{ position: "relative", zIndex: 2 }}>
                                    <Checkbox
                                        checked={selectedIdSet.has(post?.id)}
                                        onChange={() => toggleSelectPost(post?.id)}
                                    />
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    {(post?.media?.imageUrl || post?.media?.videoCapa) && (
                                        <img
                                            src={post?.media?.imageUrl || post?.media?.videoCapa}
                                            alt=""
                                            style={{ width: 45, height: 80, objectFit: "cover" }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    {post?.pinado && (
                                        <PushPinIcon fontSize="small" sx={{ marginRight: 1, color: "primary.main" }} />
                                    )}
                                    <Box
                                        sx={{
                                            minWidth: { xs: "200px", sm: "250px" },
                                            maxWidth: { xs: "200px", sm: "300px", md: "400px" },
                                            whiteSpace: "normal",
                                            wordBreak: "break-word",
                                        }}
                                    >
                                        {renderFormattedText(post?.texto)}
                                    </Box>
                                    <Link
                                        to={`/post/${post?.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{
                                            marginLeft: "8px",
                                            color: "inherit",
                                            display: "inline-flex",
                                            alignItems: "center",
                                        }}
                                    >
                                        <OpenInNewIcon fontSize="small" />
                                    </Link>
                                </TableCell>
                                <TableCell sx={{ position: "relative", maxWidth: "150px" }}>
                                    {post?.creator?.nome && (
                                        <Chip
                                            label={post.creator.nome}
                                            size="small"
                                            variant="outlined"
                                            onClick={() => addAuthorFilter(post.creator.nome)}
                                            sx={{ maxWidth: "100%", position: "relative", zIndex: 2 }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    {post?.createdAt && new Date(post.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    {post?.engajamento?.comentarios?.length || 0}
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    {post?.engajamento?.likes?.length || 0}
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    {post?.theme && (
                                        <Chip
                                            label={post.theme}
                                            size="small"
                                            sx={{
                                                maxWidth: { xs: "100px", sm: "none" },
                                                "& .MuiChip-label": {
                                                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                                },
                                            }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    {post?.format && (
                                        <Chip
                                            label={post.format}
                                            size="small"
                                            sx={{
                                                maxWidth: { xs: "100px", sm: "none" },
                                                "& .MuiChip-label": {
                                                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                                },
                                            }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    {post?.desafio?.titulo && (
                                        <Chip
                                            label={post.desafio.titulo}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{
                                                maxWidth: { xs: "120px", sm: "none" },
                                                "& .MuiChip-label": {
                                                    fontSize: { xs: "0.75rem", sm: "0.8125rem" },
                                                },
                                            }}
                                        />
                                    )}
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    <Chip
                                        label={getStatusLabel(post?.status)}
                                        size="small"
                                        sx={{
                                            backgroundColor: getStatusChipColor(post?.status || "PENDENTE").tableBg,
                                            color: getStatusChipColor(post?.status || "PENDENTE").color,
                                        }}
                                    />
                                </TableCell>
                                <TableCell
                                    className="star-rating-cell"
                                    sx={{
                                        position: "relative",
                                        padding: "8px",
                                        backgroundColor: "transparent !important",
                                        "& > *": {
                                            position: "relative",
                                            zIndex: 2,
                                        },
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: "white",
                                            borderRadius: "4px",
                                            padding: "4px",
                                        }}
                                    >
                                        <StarRating
                                            value={post?.estrelas}
                                            onChange={async (newValue) => {
                                                try {
                                                    // Atualiza localmente
                                                    const updatedPosts = allPosts.map((p) =>
                                                        p.id === post.id ? { ...p, estrelas: newValue } : p,
                                                    );
                                                    setAllPosts(updatedPosts);

                                                    // Chama API
                                                    if (newValue) {
                                                        await apiPostStarRating(post.id, newValue, post.creator.id);
                                                    }
                                                } catch (error) {
                                                    console.error("Erro ao atualizar estrelas:", error);
                                                    alert("Erro ao atualizar avaliação");
                                                }
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                                <TableCell sx={{ position: "relative" }}>
                                    <Button
                                        onClick={(e) => handleMenuOpen(e, post)}
                                        sx={{ zIndex: 2, position: "relative" }}
                                    >
                                        <MoreVertIcon />
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    mt: 3,
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    {sortedPosts.length === 0
                        ? "Nenhum post encontrado"
                        : `Mostrando ${(currentPage - 1) * rowsPerPage + 1}–${Math.min(
                              currentPage * rowsPerPage,
                              sortedPosts.length,
                          )} de ${sortedPosts.length} posts`}
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Por página</InputLabel>
                        <Select value={rowsPerPage} label="Por página" onChange={handleRowsPerPageChange}>
                            {ROWS_PER_PAGE_OPTIONS.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {totalPages > 1 && (
                        <Pagination
                            count={totalPages}
                            page={currentPage}
                            onChange={handlePageChange}
                            color="primary"
                            disabled={loading}
                        />
                    )}
                </Box>
            </Box>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                {getAvailableActions(selectedPost?.status, selectedPost?.pinado, selectedPost).map((actionKey) => {
                    const menuItem = MENU_ITEMS[actionKey];
                    return (
                        <MenuItem key={actionKey} onClick={() => handleAction(menuItem.action)}>
                            <ListItemIcon>{menuItem.icon}</ListItemIcon>
                            <ListItemText>{menuItem.text}</ListItemText>
                        </MenuItem>
                    );
                })}
            </Menu>

            <BulkProgressDialog progress={bulkProgress} />

            <BulkResultDialog result={bulkResult} onClose={() => setBulkResult(null)} />

            <RejectReasonDialog
                open={rejectDialogOpen}
                title="Motivo da Rejeição"
                confirmLabel="Reprovar"
                value={rejectReason}
                onChange={setRejectReason}
                onClose={() => setRejectDialogOpen(false)}
                onConfirm={handleReject}
            />

            <RejectReasonDialog
                open={bulkRejectDialogOpen}
                title={`Reprovar ${rejectableSelection.length} post(s)`}
                description="Cada autor recebe um único e-mail com a lista dos seus posts reprovados e este motivo. Os posts já aprovados terão a pontuação removida."
                confirmLabel="Reprovar todos"
                value={rejectReason}
                onChange={setRejectReason}
                onClose={() => setBulkRejectDialogOpen(false)}
                onConfirm={handleBulkReject}
            />

            <Dialog open={bulkApproveDialogOpen} onClose={() => setBulkApproveDialogOpen(false)}>
                <DialogTitle>Aprovar {approvableSelection.length} post(s)</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        Os posts selecionados serão aprovados e passarão a pontuar no ranking.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setBulkApproveDialogOpen(false)}>Cancelar</Button>
                    <Button onClick={handleBulkApprove} variant="contained" color="primary">
                        Aprovar todos
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
